import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { api } from '../lib/api'
import { useAuth } from '../hooks/useAuth'
import { ProgrammeDetailsSheet } from '../components/ProgrammeDetailsSheet'
import { PortalButton } from '../components/PortalButton'
import { universityBadge } from '../lib/universityBadge'
import { UniversityLogo } from '../components/UniversityLogo'
import { PeriodPickerModal } from '../components/PeriodPickerModal'
import { visibleFirstName } from '../lib/visibleFirstName'

/**
 * CoursesPage - onboarding-v2 university-landing page.
 *
 * Shows all active programmes at the student's chosen university, grouped by
 * faculty, searchable. Two commit paths:
 *   1. Pick a real course → POST /auth/commit-programme → /subjects
 *   2. "Só quero praticar" → POST /auth/commit-placeholder → /subjects
 *
 * Slice 1 intentionally omits:
 *   - The 3-question quick discovery filter (Slice 2)
 *   - The 1ª/2ª opção multi-commit banner (Slice 3)
 *   - The "Ver questões" free preview (Slice 2)
 *
 * The page handles both first-time pick (from /universities) and later
 * additions (from the context switcher on /subjects) - the flow is the same.
 */

interface Requirement {
  subject: { code: string; name: string }
  weight:  number
  variant: string
  required: boolean
}

interface Programme {
  id:                string
  name:              string
  faculty:           string | null
  short_name:        string | null
  seats:             number | null
  description:       string | null
  is_placeholder:    boolean
  periods_available: string[]                                // ["laboral"] or ["laboral","pos_laboral"]
  seats_by_period:   { laboral?: number; pos_laboral?: number } | null
  requirements:      Requirement[]
}

interface UniversityMeta {
  id:                         string
  name:                       string
  short_name:                 string
  max_options_per_candidate:  number
  logo_url?:                  string | null
}

export const CoursesPage: React.FC = () => {
  const { shortName } = useParams<{ shortName: string }>()
  const navigate = useNavigate()
  const { user, loading: authLoading, refresh } = useAuth()

  const [university, setUniversity] = useState<UniversityMeta | null>(null)
  const [programmes, setProgrammes] = useState<Programme[]>([])
  const [loading, setLoading]       = useState(true)
  const [error, setError]           = useState<string | null>(null)
  const [committing, setCommitting] = useState<string | null>(null)
  const [search, setSearch]         = useState('')

  // 2026-04-29 - quick-discovery state removed alongside QuickDiscoveryModal
  // (Batch 2d cleanup). Discovery now runs entirely from /subjects via the
  // CAT placement (mode=discovery) → /recommendations grouped output.

  // Programme-info sheet (opened via the "i" icon on each card) - shows
  // description, typical employers, required subjects, seats, etc.
  const [detailsProgrammeId, setDetailsProgrammeId] = useState<string | null>(null)

  // Period picker - opened when a dual-period course is committed.
  const [periodPending, setPeriodPending] = useState<Programme | null>(null)

  // 1ª/2ª opção flow: after first commit at a uni that allows 2 options,
  // we stay on the page and surface a banner. nextPriority defaults to 1
  // and bumps to 2 once the student elects to pick a 2ª opção.
  const [firstOption, setFirstOption]     = useState<{ id: string; name: string } | null>(null)
  const [nextPriority, setNextPriority]   = useState<1 | 2>(1)

  // 2026-04-29: priority_conflict modal state. Triggered when the
  // backend rejects /auth/commit-programme because the student already
  // has another programme at the same uni + same priority. Without
  // this, "alterar" → pick a new course → student dead-ended on the
  // raw "priority_conflict" string. Now we surface the same 3-option
  // swap UX as CommitConfirmSheet:
  //   - Trocar (replace at the conflicting priority)
  //   - Switch slot (try the OTHER priority)
  //   - Cancelar
  const [conflict, setConflict] = useState<{
    programmeId:                string
    programmeName:              string
    period:                     'laboral' | 'pos_laboral' | 'ead' | undefined
    triedPriority:              1 | 2
    conflictingProgrammeName:   string
    conflictingUniversity:      string
  } | null>(null)

  // Faculty slicer. Each fresh visit auto-picks a random faculty so the student
  // lands on a bite-sized subset (~5–15 programmes) instead of the full 88-item
  // list on mobile. Set to null via the "Limpar filtro" chip to see everything.
  const [facultyFilter, setFacultyFilter] = useState<string | null>(null)
  // Tracks whether we've already auto-picked for this page visit - prevents a
  // re-pick on refetch (e.g. after a commit).
  const facultyAutoPicked = useRef(false)

  useEffect(() => {
    if (!shortName) return
    setLoading(true)
    api.get<{ university: UniversityMeta; programmes: Programme[] }>(`/universities/${encodeURIComponent(shortName)}/programmes`)
      .then(d => {
        setUniversity(d.university)
        setProgrammes(d.programmes)
        // Random-pick a faculty on first load so the student sees a focused
        // subset. Only runs once per visit; resets when the URL changes.
        if (!facultyAutoPicked.current) {
          const faculties = [...new Set(d.programmes.map(p => p.faculty ?? 'Outros'))]
          if (faculties.length > 1) {
            const pick = faculties[Math.floor(Math.random() * faculties.length)]
            setFacultyFilter(pick)
          }
          facultyAutoPicked.current = true
        }
      })
      .catch(err => setError(err?.data?.error ?? err?.message ?? 'Erro ao carregar cursos'))
      .finally(() => setLoading(false))
  }, [shortName])

  // Reset the auto-pick guard when the student navigates to a different
  // university - they should get a fresh random faculty there too.
  useEffect(() => {
    facultyAutoPicked.current = false
    setFacultyFilter(null)
  }, [shortName])

  // Faculty counts for the slicer chips (based on the pre-filter set, so the
  // counts remain stable while the user browses).
  const facultyCounts = useMemo(() => {
    const m = new Map<string, number>()
    for (const p of programmes) {
      const k = p.faculty ?? 'Outros'
      m.set(k, (m.get(k) ?? 0) + 1)
    }
    return [...m.entries()].sort(([a], [b]) => a.localeCompare(b))
  }, [programmes])

  const filteredProgrammes = useMemo(() => {
    const s = search.trim().toLowerCase()
    let base = programmes
    // Faculty slicer
    if (facultyFilter !== null) {
      base = base.filter(p => (p.faculty ?? 'Outros') === facultyFilter)
    }
    if (!s) return base
    return base.filter(p =>
      p.name.toLowerCase().includes(s) ||
      (p.faculty ?? '').toLowerCase().includes(s) ||
      p.requirements.some(r => r.subject.code.toLowerCase().includes(s) || r.subject.name.toLowerCase().includes(s))
    )
  }, [programmes, search, facultyFilter])

  // Group by faculty
  const grouped = useMemo(() => {
    const byFaculty: Record<string, Programme[]> = {}
    for (const p of filteredProgrammes) {
      const key = p.faculty ?? 'Outros'
      if (!byFaculty[key]) byFaculty[key] = []
      byFaculty[key].push(p)
    }
    return Object.entries(byFaculty).sort(([a], [b]) => a.localeCompare(b))
  }, [filteredProgrammes])

  /**
   * 2026-04-29: signature extended with `replaceExisting` (forces the
   * backend to drop a conflicting StudentProgramme at the same priority)
   * and `overridePriority` (lets the conflict-modal retry at the OTHER
   * slot). Both default to undefined so existing call-sites (programme
   * card click, period picker callback, post-1ª-opção banner) behave
   * exactly as before.
   */
  const commitProgramme = async (
    programmeId:      string,
    period?:          'laboral' | 'pos_laboral' | 'ead',
    replaceExisting?: boolean,
    overridePriority?: 1 | 2,
  ) => {
    const targetProgramme = programmes.find(p => p.id === programmeId)
    if (!targetProgramme) return

    // Dual-period course without an explicit period → open PeriodPickerModal
    // and let the student choose before we commit.
    const periods = targetProgramme.periods_available ?? ['laboral']
    if (periods.length > 1 && !period) {
      setPeriodPending(targetProgramme)
      return
    }

    const allows2 = (university?.max_options_per_candidate ?? 1) >= 2
    const priority = overridePriority ?? nextPriority

    setCommitting(programmeId)
    setError(null)
    try {
      await api.post('/auth/commit-programme', {
        programme_id: programmeId,
        priority,
        period:       period ?? periods[0],   // single-period default = the only one
        ...(replaceExisting ? { replace_existing: true } : {}),
      })

      // Post-1ª-opção banner: only on unis that allow 2 options AND this was
      // the 1st commit. The student can then pick a 2ª opção or continue.
      if (allows2 && priority === 1) {
        setFirstOption({ id: targetProgramme.id, name: targetProgramme.name })
        setNextPriority(2)
        setCommitting(null)
        // Scroll to top so the banner is visible
        window.scrollTo({ top: 0, behavior: 'smooth' })
        return
      }
      // Refresh the auth envelope BEFORE navigating so the App route
      // guard on /subjects sees the just-committed has_programme=true.
      // Without this, the guard reads stale user state (has_programme=
      // false from before the POST) and bounces the student back to
      // /universities — manifesting as "selecting a course redirects
      // me to universities". Fix shipped 2026-06-09 alongside the Zod
      // 'ead' enum fix in /auth/commit-programme.
      try { await refresh() } catch { /* navigate anyway; /subjects has its own fallbacks */ }
      navigate('/subjects')
    } catch (err: any) {
      // 2026-04-29: structured priority_conflict from /auth/commit-programme
      // → open the conflict modal instead of dead-ending on the raw error
      // string. The student picks Trocar / Adicionar na outra opção /
      // Cancelar from there.
      const errBody = err?.data
      if (errBody?.error === 'priority_conflict') {
        setConflict({
          programmeId,
          programmeName:            targetProgramme.name,
          period:                   period ?? (periods[0] as 'laboral' | 'pos_laboral' | 'ead'),
          triedPriority:            priority as 1 | 2,
          conflictingProgrammeName: errBody.conflicting_programme_name ?? 'curso',
          conflictingUniversity:    errBody.conflicting_university ?? targetProgramme.name,
        })
        setCommitting(null)
        return
      }
      setError(errBody?.error ?? err?.message ?? 'Erro ao guardar curso')
      setCommitting(null)
    }
  }

  // Both of these navigate to /subjects, which sits behind
  // RequireProgrammePicked. That guard reads user.has_programme from the auth
  // context, so without refreshing /auth/me first it still holds the value
  // from BEFORE the commit and bounces the student straight back to
  // /universities. Confirmed in the API log: commit-programme returns 200,
  // then the next request is GET /universities with no /auth/me between them.
  //
  // commitProgramme above already did this; these two did not. Same fix,
  // same best-effort shape: /subjects has its own fallbacks, so a failed
  // refresh must not strand the student on this page.
  const skipSecondOption = async () => {
    try { await refresh() } catch { /* navigate anyway */ }
    navigate('/subjects')
  }

  const commitPlaceholder = async () => {
    setCommitting('__placeholder__')
    setError(null)
    try {
      await api.post('/auth/commit-placeholder', {})
      try { await refresh() } catch { /* navigate anyway */ }
      navigate('/subjects')
    } catch (err: any) {
      setError(err?.data?.error ?? err?.message ?? 'Erro a entrar em modo "só praticar"')
      setCommitting(null)
    }
  }

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-400 text-sm">A carregar…</div>
      </div>
    )
  }

  if (!university) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-xl border border-gray-100 p-6 max-w-sm text-center">
          <p className="text-sm text-red-600 mb-3">{error ?? 'Universidade não encontrada.'}</p>
          <button
            onClick={() => navigate('/universities')}
            className="text-sm bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700"
          >
            Voltar à lista de universidades
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-28">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/universities')}
              className="text-gray-400 hover:text-gray-600 text-sm"
            >
              ← Universidades
            </button>
            <span className="text-gray-200">|</span>
            <div className="flex items-center gap-1.5">
              <UniversityLogo shortName={university.short_name} name={university.name} logoUrl={university.logo_url ?? null} size="sm" />
              <div className="bg-indigo-600 rounded-md px-2 py-1 flex items-center justify-center">
                <span className="text-white font-black text-xs whitespace-nowrap">{universityBadge(university.short_name)}</span>
              </div>
              <h1 className="font-semibold text-gray-900 text-sm">{university.name}</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <PortalButton />
            <span className="text-sm text-gray-500 truncate max-w-[160px]">{visibleFirstName(user?.name)}</span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        <div className="mb-4">
          <h2 className="text-xl font-semibold text-gray-900 mb-1">Escolhe o teu curso</h2>
          <p className="text-sm text-gray-500">
            {programmes.length} cursos disponíveis
            {university.max_options_per_candidate >= 2 && ' · esta universidade admite até 2 opções'}
          </p>
        </div>

        {/* Post-1ª-opção banner: prompts for 2ª opção when the uni allows it */}
        {firstOption && (
          <div className="mb-4 bg-green-50 border border-green-200 rounded-xl p-4">
            <p className="text-sm font-semibold text-green-900 mb-0.5">
              ✓ 1ª opção guardada: {firstOption.name}
            </p>
            <p className="text-xs text-green-700 mb-3">
              {university?.short_name} admite até 2 opções. Queres escolher uma 2ª opção? É opcional.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-green-700">
                Escolhe abaixo outro curso para o marcar como <strong>2ª opção</strong>, ou…
              </span>
              <button
                onClick={skipSecondOption}
                className="text-xs bg-white border border-green-300 text-green-700 font-semibold px-3 py-1.5 rounded-lg hover:bg-green-100"
              >
                Continuar para praticar →
              </button>
            </div>
          </div>
        )}

        {/* Discovery banner + result-strip removed in Batch 2d cleanup
            (2026-04-29). Discovery is now driven from /subjects via the
            CAT placement (mode=discovery) → /recommendations grouped
            output. The QuickDiscoveryModal, /discovery/quick-* routes,
            and services/discovery-quick-map.ts are gone with this
            commit; nothing remains to wire here. */}

        {/* Search + faculty filter (mobile-friendly: native <select> on top of
            the OS picker; takes a single row and no vertical space until opened) */}
        <div className="mb-4 flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Procurar curso…"
            className="flex-1 text-sm border border-gray-200 rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-indigo-400"
          />
          {facultyCounts.length > 1 && (
            <label className="relative sm:w-64 block">
              {/* Persistent helper text - explains the affordance. Stays visible
                  whether or not a faculty is chosen so the student sees the
                  pre-selected faculty is changeable. */}
              <span className="text-[10px] text-gray-400 uppercase tracking-wide font-semibold block mb-0.5 pl-1">
                Clica para escolher faculdade
              </span>
              <select
                value={facultyFilter ?? ''}
                onChange={e => setFacultyFilter(e.target.value || null)}
                className={`w-full appearance-none text-sm border rounded-lg pl-4 pr-9 py-2.5 outline-none focus:ring-2 focus:ring-indigo-400 bg-white ${
                  facultyFilter === null
                    ? 'border-gray-200 text-gray-400 italic'
                    : 'border-indigo-400 text-indigo-700 font-semibold'
                }`}
              >
                {/* When the filter is null (student tapped "Limpar filtro"),
                    a single hidden option with empty value keeps the <select>
                    from silently defaulting to the first real option. The
                    label above already tells the student what to do. */}
                {facultyFilter === null && <option value="" hidden>-</option>}
                {facultyCounts.map(([fac, count]) => (
                  <option key={fac} value={fac}>{fac} ({count})</option>
                ))}
              </select>
              <span className="pointer-events-none absolute right-3 bottom-3 text-gray-400 text-xs">▾</span>
            </label>
          )}
        </div>
        {facultyFilter && (
          <div className="mb-3 -mt-2 flex items-center gap-2">
            <span className="text-xs text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-full px-2.5 py-0.5">
              Filtrado: {facultyFilter}
            </span>
            <button
              onClick={() => setFacultyFilter(null)}
              className="text-xs text-gray-400 hover:text-gray-700"
            >
              Limpar filtro
            </button>
          </div>
        )}

        {error && (
          <div className="mb-4 text-sm bg-red-50 text-red-700 border border-red-100 rounded-lg px-3 py-2">
            {error}
            <button onClick={() => setError(null)} className="ml-2 text-red-400 hover:text-red-700">×</button>
          </div>
        )}

        {/* Programme grid grouped by faculty */}
        {grouped.length === 0 ? (
          <p className="text-sm text-gray-400 italic text-center py-12">
            {search ? 'Nenhum curso encontrado com esse termo.' : 'Nenhum curso disponível.'}
          </p>
        ) : (
          <div className="space-y-5">
            {grouped.map(([faculty, progs]) => (
              <section key={faculty}>
                {facultyFilter === null && (
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{faculty}</h3>
                )}
                <div className="grid sm:grid-cols-2 gap-3">
                  {progs.map(p => {
                    const isFirstOption = firstOption?.id === p.id
                    return (
                    <article
                      key={p.id}
                      className={`bg-white border rounded-xl p-4 flex flex-col ${
                        isFirstOption ? 'border-green-300 bg-green-50/40' : 'border-gray-100'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h4 className="font-semibold text-gray-900 text-sm leading-snug">{p.name}</h4>
                        <div className="flex items-center gap-1.5 shrink-0">
                          {isFirstOption && (
                            <span className="text-[10px] bg-green-100 text-green-700 border border-green-200 px-1.5 py-0.5 rounded font-semibold">
                              ✓ 1ª opção
                            </span>
                          )}
                          {/* "Detalhes" chip - top-right of the card header
                              (PR - discoverability, 2026-05-07). F-pattern
                              reading lands here right after the title, so
                              the secondary affordance is the second thing
                              the student sees. Keeps the bottom-row CTA
                              uncluttered. Compact label ("Detalhes" not
                              "Detalhes do curso") because we're already
                              inside a course card - adding "do curso" would
                              repeat the obvious context. Stays available
                              even on a committed (✓ 1ª opção) card so the
                              student can re-check the info before deciding
                              to swap. */}
                          <button
                            onClick={() => setDetailsProgrammeId(p.id)}
                            disabled={committing !== null}
                            className="inline-flex items-center gap-1 text-[10px] text-indigo-700 border border-indigo-200 hover:bg-indigo-50 hover:border-indigo-400 px-1.5 py-0.5 rounded font-medium transition-colors disabled:opacity-40"
                            aria-label="Ver detalhes deste curso"
                          >
                            <span className="font-serif italic">i</span>
                            <span>Detalhes</span>
                          </button>
                        </div>
                      </div>
                      {p.description && (
                        <p className="text-xs text-gray-500 line-clamp-2 mb-2">{p.description}</p>
                      )}
                      {p.requirements.length > 0 && (
                        <div className="flex flex-wrap items-center gap-1 mb-2">
                          <span className="text-[10px] text-gray-400 uppercase tracking-wide font-semibold mr-0.5">
                            Exames:
                          </span>
                          {p.requirements.map((r, i) => (
                            <span key={i} className="text-[10px] bg-indigo-50 text-indigo-700 border border-indigo-100 px-1.5 py-0.5 rounded">
                              {r.subject.code}{r.variant ? `-${r.variant}` : ''} · {r.weight}%
                            </span>
                          ))}
                        </div>
                      )}
                      {/* Period hint - surfaces the available periods before
                          the student clicks "Escolher curso" so they aren't
                          blindsided by the picker modal. */}
                      {p.periods_available && p.periods_available.length > 1 && (
                        <div className="flex items-center gap-1 mb-3 text-[10px] text-gray-500">
                          <span className="uppercase tracking-wide font-semibold text-gray-400">Períodos:</span>
                          <span>
                            {p.periods_available.includes('laboral')     && '☀️ Laboral'}
                            {p.periods_available.includes('pos_laboral') && (p.periods_available.includes('laboral') ? ' · 🌙 Pós-Laboral' : '🌙 Pós-Laboral')}
                            {p.periods_available.includes('ead')         && ' · 🖥️ EaD'}
                          </span>
                        </div>
                      )}
                      <div className="mt-auto flex items-center justify-between gap-2 text-xs">
                        {/* Seats slot on the card: for dual-period courses the
                            legacy `seats` column is the sum across pools and
                            misleading ("80 vagas" for Eng. Informática when
                            it's actually 40 + 40 in separate pools). Skip it
                            here - the real split lives in the "Detalhes" sheet. */}
                        <span className="text-gray-400">
                          {p.periods_available && p.periods_available.length > 1
                            ? ''
                            : p.seats ? `${p.seats} vagas` : ''}
                        </span>
                        <button
                          onClick={() => commitProgramme(p.id)}
                          disabled={committing !== null || isFirstOption}
                          className={`font-semibold px-3 py-1.5 rounded-lg disabled:opacity-40 ${
                            isFirstOption
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : nextPriority === 2
                                ? 'bg-fuchsia-600 text-white hover:bg-fuchsia-700'
                                : 'bg-indigo-600 text-white hover:bg-indigo-700'
                          }`}
                        >
                          {committing === p.id
                            ? 'A guardar…'
                            : isFirstOption
                              ? 'Já escolhido'
                              : nextPriority === 2
                                ? 'Escolher 2ª opção'
                                : 'Escolher curso'}
                        </button>
                      </div>
                    </article>
                    )
                  })}
                </div>
              </section>
            ))}
          </div>
        )}
      </main>

      {/* Floating "Só praticar" CTA */}
      <div className="fixed bottom-6 right-6 z-20">
        <button
          onClick={commitPlaceholder}
          disabled={committing !== null}
          className="bg-white border border-gray-200 shadow-lg rounded-full px-5 py-3 text-sm font-semibold text-gray-700 hover:border-indigo-400 hover:text-indigo-700 disabled:opacity-40"
          title="Entrar na app sem escolher curso - decides mais tarde"
        >
          {committing === '__placeholder__' ? 'A entrar…' : '✦ Só quero praticar'}
        </button>
      </div>

      {/* Modals */}
      {detailsProgrammeId && (
        <ProgrammeDetailsSheet
          programmeId={detailsProgrammeId}
          onClose={() => setDetailsProgrammeId(null)}
          onChoose={() => {
            const id = detailsProgrammeId
            setDetailsProgrammeId(null)
            if (id) commitProgramme(id)
          }}
          chooseLabel={nextPriority === 2 ? 'Escolher 2ª opção' : 'Escolher curso'}
        />
      )}
      {periodPending && university && (
        <PeriodPickerModal
          programmeName={periodPending.name}
          universityShortName={university.short_name}
          priorityLabel={nextPriority === 2 ? '2ª opção' : '1ª opção'}
          periodsAvailable={periodPending.periods_available}
          seats={periodPending.seats_by_period ?? undefined}
          onClose={() => setPeriodPending(null)}
          onPick={(period) => {
            const id = periodPending.id
            setPeriodPending(null)
            commitProgramme(id, period)
          }}
        />
      )}

      {/* Priority-conflict modal - opens whenever the backend rejects
          /auth/commit-programme with priority_conflict (the slot the
          student tried already holds another programme at this uni).
          Mirrors CommitConfirmSheet's 3-option swap UX so both code
          paths behave identically. 2026-04-29. */}
      {conflict && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => setConflict(null)}
        >
          <div
            className="bg-white w-full max-w-md rounded-t-xl sm:rounded-xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-gray-100 flex items-start justify-between">
              <div className="flex-1 pr-3">
                <h3 className="text-base font-semibold text-gray-900">Como queres adicionar este curso?</h3>
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                  Já tens <strong>{conflict.conflictingProgrammeName}</strong> como {conflict.triedPriority}ª opção em {conflict.conflictingUniversity}. Podes <strong>trocar</strong> (remove o curso actual e coloca <strong>{conflict.programmeName}</strong> no lugar) ou <strong>adicionar na outra opção</strong> e manter os dois.
                </p>
              </div>
              <button
                onClick={() => setConflict(null)}
                aria-label="Fechar"
                className="text-gray-400 hover:text-gray-600 text-xl leading-none w-8 h-8 flex items-center justify-center"
              >
                ✕
              </button>
            </div>
            <div className="px-5 py-4 space-y-2">
              <button
                onClick={() => {
                  const c = conflict
                  setConflict(null)
                  // Replace at the conflicting priority. Backend deletes
                  // the existing StudentProgramme atomically before
                  // upserting the new one (see routes/auth.ts).
                  commitProgramme(c.programmeId, c.period, /* replaceExisting */ true, c.triedPriority)
                }}
                className="w-full py-3 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 transition-colors"
              >
                Sim, trocar a {conflict.triedPriority}ª opção
              </button>
              <button
                onClick={() => {
                  const c = conflict
                  const otherPriority = (c.triedPriority === 1 ? 2 : 1) as 1 | 2
                  setConflict(null)
                  // Try the OTHER slot. If THAT also conflicts, the
                  // backend returns another priority_conflict and this
                  // modal re-opens for the new priority - student can
                  // iterate until they find a free slot or swap.
                  commitProgramme(c.programmeId, c.period, /* replaceExisting */ false, otherPriority)
                }}
                className="w-full py-3 bg-white border border-gray-200 text-gray-800 font-semibold rounded-xl hover:bg-gray-50 transition-colors"
              >
                {conflict.triedPriority === 1 ? 'Adicionar como 2ª opção' : 'Tornar curso principal'}
              </button>
              <button
                onClick={() => setConflict(null)}
                className="w-full py-2 text-sm text-gray-500 hover:text-gray-700"
              >
                Manter os meus cursos actuais
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
